import React, { useState, useEffect, useRef } from 'react';
import { Files } from 'lucide-react';
import DocumentTile, { DocType } from '../../../documents/DocumentTile';
import TextDocumentModal, { TextDocData } from '../../../documents/modals/TextDocumentModal';
import NewTextDocumentModal from '../../../documents/modals/new/options/NewTextDocumentModal';
import PDFDocumentModal, { PdfDocData } from '../../../documents/modals/PDFDocumentModal';
import NewPDFDocumentModal, { PdfDocData as NewPdfDocData } from '../../../documents/modals/new/options/NewPDFDocumentModal';
import NewDocumentModal from '../../../documents/modals/new/NewDocumentModal';
import ConfirmDeleteModal from '../../../../organisms/ConfirmDeleteModal';
import api, { getUserUid } from '../../../../../utils/api';
import { showToast } from '../../../../../utils/toastManager';
import { API_ENDPOINTS, S3_BASE_URL } from '../../../../../config';

interface DocumentsTabProps {
  jobId?: string;
  boardId?: string;
  highlightEntityUid?: string | null;
}

interface TableDoc {
  id: string;
  name: string;
  type: string;
  updated: string;
  documentType?: 'text' | 'doc' | 'ai';
  _raw?: DocumentFromAPI;
}

interface DocumentFromAPI {
  uid: string;
  user_uid: string;
  kanban_uid?: string;
  job_uid?: string;
  type: 'text' | 'doc' | 'ai';
  url?: string;
  name: string;
  doc_category: string;
  description?: string;
  text_value?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export default function DocumentsTab({ jobId, boardId, highlightEntityUid }: DocumentsTabProps) {
  const [newDocModalOpen, setNewDocModalOpen] = useState(false);
  const [newTextDocModalOpen, setNewTextDocModalOpen] = useState(false);
  const [newPdfDocModalOpen, setNewPdfDocModalOpen] = useState(false);
  const [textDocModalOpen, setTextDocModalOpen] = useState(false);
  const [pdfDocModalOpen, setPdfDocModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [documentToDeleteName, setDocumentToDeleteName] = useState<string>('');
  
  const [currentTextDoc, setCurrentTextDoc] = useState<TextDocData>({
    title: '',
    category: 'Resume',
    description: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    content: '',
  });
  
  const [currentPdfDoc, setCurrentPdfDoc] = useState<PdfDocData>({
    title: '',
    category: 'Resume',
    description: '',
  });
  
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | undefined>(undefined);
  const [editingTextDocId, setEditingTextDocId] = useState<string | null>(null);
  const [editingPdfDocId, setEditingPdfDocId] = useState<string | null>(null);
  
  const [documents, setDocuments] = useState<TableDoc[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  React.useEffect(() => {
    if (!highlightEntityUid || documents.length === 0) return;
    const el = document.querySelector(`[data-uid="${highlightEntityUid}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ejm-entity-highlight');
      const t = setTimeout(() => el.classList.remove('ejm-entity-highlight'), 3500);
      return () => clearTimeout(t);
    }
  }, [highlightEntityUid, documents]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const transformDocument = (apiDoc: DocumentFromAPI): TableDoc => {
    const extension = (apiDoc.type === 'text' || apiDoc.type === 'ai') ? '.txt' : '.pdf';
    const fileName = apiDoc.name.endsWith(extension) ? apiDoc.name : `${apiDoc.name}${extension}`;
    
    return {
      id: apiDoc.uid,
      name: fileName,
      type: apiDoc.doc_category,
      updated: formatDate(apiDoc.created_at),
      documentType: apiDoc.type,
      _raw: apiDoc,
    };
  };

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!boardId) {
        setDocuments([]);
        setLoadingDocuments(false);
        return;
      }

      try {
        setLoadingDocuments(true);
        const payload = {
          kanban_uid: boardId,
          job_uid: null,
        };
        
        const data = await api.post<DocumentFromAPI[]>(API_ENDPOINTS.documents.getByKanban, payload);
        
        if (Array.isArray(data)) {
          const filteredData = jobId 
            ? data.filter(doc => doc.job_uid && String(doc.job_uid) === String(jobId))
            : data;
          
          const transformedDocs = filteredData.map(transformDocument);
          setDocuments(transformedDocs);
        } else {
          setDocuments([]);
        }
      } catch (error) {

        setDocuments([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, [boardId, jobId]);

  const refreshDocuments = async () => {
    if (!boardId) return;

    try {
      const payload = {
        kanban_uid: boardId,
        job_uid: null,
      };
      const data = await api.post<DocumentFromAPI[]>(API_ENDPOINTS.documents.getByKanban, payload);
      if (Array.isArray(data)) {
        const filteredData = jobId
          ? data.filter(doc => doc.job_uid && String(doc.job_uid) === String(jobId))
          : data;

        const transformedDocs = filteredData.map(transformDocument);
        setDocuments(transformedDocs);
      }
    } catch (error) {

    }
  };

  const refreshDocsRef = useRef(refreshDocuments);
  useEffect(() => {
    refreshDocsRef.current = refreshDocuments;
  });

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { jobId?: string } | undefined;
      if (detail?.jobId !== jobId) return;
      refreshDocsRef.current();
    };
    window.addEventListener('ai-document-created', handler);
    return () => window.removeEventListener('ai-document-created', handler);
  }, [jobId]);

  const handleOpenDocument = (id: string) => {
    const doc = documents.find(d => d.id === id);
    if (!doc || !doc._raw) return;

    const rawDoc = doc._raw as DocumentFromAPI;

    const isTextDoc = rawDoc.type === 'text' || rawDoc.type === 'ai';
    const isPdfDoc = rawDoc.type === 'doc';
    
    if (isTextDoc) {
      setEditingTextDocId(id);
      setCurrentTextDoc({
        title: rawDoc.name,
        category: rawDoc.doc_category,
        description: rawDoc.description || '',
        firstName: rawDoc.first_name || '',
        lastName: rawDoc.last_name || '',
        email: rawDoc.email || '',
        phone: rawDoc.phone_number || '',
        content: rawDoc.text_value || '',
        boardLinked: rawDoc.kanban_uid,
        jobLinked: rawDoc.job_uid,
      });
      setTextDocModalOpen(true);
    } else if (isPdfDoc) {
      setEditingPdfDocId(id);
      setCurrentPdfDoc({
        title: rawDoc.name,
        category: rawDoc.doc_category,
        description: rawDoc.description || '',
        boardLinked: rawDoc.kanban_uid,
        jobLinked: rawDoc.job_uid,
      });
      
      const documentUrl = rawDoc.url && rawDoc.url.trim()
        ? `${S3_BASE_URL}${rawDoc.url.trim()}`
        : undefined;
      setCurrentPdfUrl(documentUrl);
      setPdfDocModalOpen(true);
    }
  };

  const handleEditDocument = (id: string) => {
    handleOpenDocument(id);
  };

  const handleDeleteDocument = (id: string) => {
    const doc = documents.find(d => d.id === id);
    if (doc) {
      setDocumentToDelete(id);
      setDocumentToDeleteName(doc.name);
      setDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) {
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
      setDocumentToDeleteName('');
      return;
    }

    const documentIdToDelete = documentToDelete;
    
    setDocuments(prev => prev.filter(d => d.id !== documentIdToDelete));
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
    setDocumentToDeleteName('');

    try {
      await api.delete(API_ENDPOINTS.documents.delete, { document_uid: documentIdToDelete });
      await refreshDocuments();
    } catch (error: any) {

      await refreshDocuments();
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
    setDocumentToDeleteName('');
  };

  const handleTextDocCommit = async (data: TextDocData) => {
    if (!editingTextDocId) {

      return;
    }

    try {
      if (!data.title?.trim()) {

        return;
      }

      const documentPayload = {
        document_uid: editingTextDocId,
        type: 'text' as const,
        name: data.title.trim(),
        doc_category: data.category || 'Other',
        description: data.description?.trim() || null,
        text_value: data.content?.trim() || null,
        first_name: data.firstName?.trim() || null,
        last_name: data.lastName?.trim() || null,
        email: data.email?.trim() || null,
        phone_number: data.phone?.trim() || null,
        kanban_uid: data.boardLinked || null,
        job_uid: data.jobLinked || null,
      };

      await api.put(API_ENDPOINTS.documents.update, documentPayload);
      
      setTextDocModalOpen(false);
      setEditingTextDocId(null);
      
      await refreshDocuments();
    } catch (error: any) {

    }
  };

  const handlePdfDocCommit = async (data: PdfDocData) => {
    if (!editingPdfDocId) {

      return;
    }

    try {
      if (!data.title?.trim()) {

        return;
      }

      const documentPayload: any = {
        document_uid: editingPdfDocId,
        type: 'doc' as const,
        name: data.title.trim(),
        doc_category: data.category || 'Other',
      };

      if (data.description?.trim()) {
        documentPayload.description = data.description.trim();
      }

      documentPayload.kanban_uid = data.boardLinked || null;
      documentPayload.job_uid = data.jobLinked || null;

      await api.put(API_ENDPOINTS.documents.update, documentPayload);
      
      setPdfDocModalOpen(false);
      setEditingPdfDocId(null);
      
      await refreshDocuments();
    } catch (error: any) {

    }
  };

  const handleNewTextDocCommit = async (data: TextDocData) => {
    try {
      if (!boardId) {
        return;
      }
      if (!data.title?.trim()) {
        showToast('Enter a title for the document', 'error');
        return;
      }

      const user_uid = getUserUid();
      if (!user_uid) {
        showToast('Your session expired. Please sign in again.', 'error');
        return;
      }

      const documentPayload = {
        user_uid,
        type: 'text' as const,
        name: data.title.trim(),
        doc_category: data.category || 'Other',
        description: data.description?.trim() || null,
        text_value: data.content?.trim() || null,
        first_name: data.firstName?.trim() || null,
        last_name: data.lastName?.trim() || null,
        email: data.email?.trim() || null,
        phone_number: data.phone?.trim() || null,
        kanban_uid: boardId,
        job_uid: jobId || null,
      };

      await api.post(API_ENDPOINTS.documents.create, documentPayload);
      
      setNewTextDocModalOpen(false);
      
      await refreshDocuments();
    } catch (error: any) {
      if (error?.status === 401) {
        showToast('Your session expired. Please sign in again.', 'error');
      } else {
        showToast('Failed to create the document. Please try again.', 'error');
      }
    }
  };

  const handleNewPdfDocCommit = async (data: NewPdfDocData) => {
    try {
      if (!boardId) {
        return;
      }
      if (!data.file) {
        return;
      }
      if (!data.title?.trim()) {
        return;
      }

      const formData = new FormData();
      formData.append('type', 'doc');
      formData.append('name', data.title.trim());
      formData.append('doc_category', data.category || 'Other');
      
      if (data.description?.trim()) {
        formData.append('description', data.description.trim());
      }
      
      formData.append('kanban_uid', boardId);
      
      if (jobId) {
        formData.append('job_uid', jobId);
      }
      
      formData.append('file', data.file);

      await api.postFormData(API_ENDPOINTS.documents.upload, formData);
      
      setNewPdfDocModalOpen(false);
      
      await refreshDocuments();
    } catch (error: any) {

    }
  };

  return (
    <div className="documents-tab-wrapper">
      <div className="documents-tab-header">
        <button
          className="btn-new-document"
          onClick={() => setNewDocModalOpen(true)}
          type="button"
        >
          + Document
        </button>
      </div>

      {loadingDocuments ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
          Loading documents...
        </div>
      ) : (
        <>
          {documents.length === 0 && (
            <div className="documents-tab-empty">
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                <Files size={32} style={{ color: '#9ca3af' }} />
              </div>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>No documents yet</div>
              <div>Click "+ Document" to add your first document</div>
            </div>
          )}

          <div className="documents-tab-grid">
            {documents.map(d => (
              <div key={d.id} data-uid={d.id}>
                <DocumentTile
                  id={d.id}
                  name={d.name}
                  type={d.type as DocType}
                  updated={d.updated}
                  documentType={d.documentType}
                  onOpen={handleOpenDocument}
                  onEdit={handleEditDocument}
                  onDelete={handleDeleteDocument}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <NewDocumentModal
        open={newDocModalOpen}
        onClose={() => setNewDocModalOpen(false)}
        title="Create New Document"
        size="md"
      >
        <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px', textAlign: 'left' }}>
          Choose the type of document you want to create:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            className="modal__btn"
            style={{ textAlign: 'left', padding: '12px 16px', height: 'auto' }}
            onClick={() => {
              setNewDocModalOpen(false);
              setNewTextDocModalOpen(true);
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Create Text Document</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Create a new text document using the inline text editor.</div>
          </button>
          <button
            className="modal__btn"
            style={{ textAlign: 'left', padding: '12px 16px', height: 'auto' }}
            onClick={() => {
              setNewDocModalOpen(false);
              setNewPdfDocModalOpen(true);
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Upload File</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Upload an existing file from your computer. Allowed file types: .pdf, .doc, .docx, .png, .jpg</div>
          </button>
        </div>
      </NewDocumentModal>

      <NewTextDocumentModal
        open={newTextDocModalOpen}
        savedValue={{
          title: '',
          category: 'Resume',
          description: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          content: '',
          boardLinked: boardId,
          jobLinked: jobId,
        }}
        onClose={() => setNewTextDocModalOpen(false)}
        onCommit={handleNewTextDocCommit}
      />

      <NewPDFDocumentModal
        open={newPdfDocModalOpen}
        savedValue={{
          title: '',
          category: 'Resume',
          description: '',
          boardLinked: boardId,
          jobLinked: jobId,
        }}
        onClose={() => setNewPdfDocModalOpen(false)}
        onCommit={handleNewPdfDocCommit}
      />

      <TextDocumentModal
        open={textDocModalOpen}
        savedValue={currentTextDoc}
        onClose={() => {
          setTextDocModalOpen(false);
          setEditingTextDocId(null);
        }}
        onCommit={handleTextDocCommit}
      />

      <PDFDocumentModal
        open={pdfDocModalOpen}
        savedValue={currentPdfDoc}
        pdfUrl={currentPdfUrl}
        onClose={() => {
          setPdfDocModalOpen(false);
          setEditingPdfDocId(null);
        }}
        onCommit={handlePdfDocCommit}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        itemName={documentToDeleteName}
        itemType="document"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
